package com.patronat.top.features.venues.presentation.mappers

import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.CreateVenueRequest
import com.patronat.top.dto.PatchVenueRequest
import com.patronat.top.dto.UpdateVenueRequest
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.DayOfWeek
import com.patronat.top.dto.venues.GeoCoordinates
import com.patronat.top.dto.venues.ServiceItem
import com.patronat.top.dto.venues.VenueCategory
import com.patronat.top.dto.venues.VenueLocation
import com.patronat.top.dto.venues.VenueMediaRef
import com.patronat.top.dto.venues.VenueSchedule
import com.patronat.top.dto.venues.VenueServices
import com.patronat.top.dto.venues.WorkDay
import com.patronat.top.features.venues.presentation.VenueFormMode
import com.patronat.top.features.venues.presentation.VenueFormState
import com.patronat.top.features.venues.presentation.VenueScheduleDayLabels
import com.patronat.top.ui.models.SelectOption
import com.patronat.top.ui.models.OwnerInfoData
import com.patronat.top.ui.models.VenueGalleryPhoto
import com.patronat.top.ui.models.VenueLocationData
import com.patronat.top.ui.models.VenueMainInfoData
import com.patronat.top.ui.models.VenueServiceItemData
import com.patronat.top.ui.models.WorkScheduleDay
import kotlinx.datetime.LocalTime
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_error_address_required
import top.features.venues.generated.resources.venue_error_category_required
import top.features.venues.generated.resources.venue_error_name_required
import top.features.venues.generated.resources.venue_error_phone_required
import top.features.venues.generated.resources.venue_error_schedule_close_time_invalid
import top.features.venues.generated.resources.venue_error_schedule_close_time_required
import top.features.venues.generated.resources.venue_error_schedule_day_unknown
import top.features.venues.generated.resources.venue_error_schedule_open_time_invalid
import top.features.venues.generated.resources.venue_error_schedule_open_time_required
import top.features.venues.generated.resources.venue_error_schedule_order_invalid
import top.features.venues.generated.resources.venue_error_service_currency_invalid
import top.features.venues.generated.resources.venue_error_service_name_required
import top.features.venues.generated.resources.venue_error_service_price_invalid

sealed interface VenueCreateRequestBuildResult {
    data class Success(val request: CreateVenueRequest) : VenueCreateRequestBuildResult
    data class Error(val message: UiText) : VenueCreateRequestBuildResult
}

sealed interface VenueUpdateRequestBuildResult {
    data class Success(val request: UpdateVenueRequest) : VenueUpdateRequestBuildResult
    data class Error(val message: UiText) : VenueUpdateRequestBuildResult
}

sealed interface VenuePatchRequestBuildResult {
    data class Success(val request: PatchVenueRequest) : VenuePatchRequestBuildResult
    data class Error(val message: UiText) : VenuePatchRequestBuildResult
}

internal fun VenueFormState.toCreateVenueRequest(submitForModeration: Boolean = true): VenueCreateRequestBuildResult {
    val name = mainInfo.name.trim()
    if (name.isEmpty()) {
        return VenueCreateRequestBuildResult.Error(UiText.StringResource(Res.string.venue_error_name_required))
    }

    val category = mainInfo.category?.id
        ?.let { value -> VenueCategory.entries.firstOrNull { it.name == value } }
        ?: return VenueCreateRequestBuildResult.Error(UiText.StringResource(Res.string.venue_error_category_required))

    val phone = mainInfo.phone.trim()
    if (phone.isEmpty()) {
        return VenueCreateRequestBuildResult.Error(UiText.StringResource(Res.string.venue_error_phone_required))
    }

    val address = locationData.address.trim()
    if (address.isEmpty()) {
        return VenueCreateRequestBuildResult.Error(UiText.StringResource(Res.string.venue_error_address_required))
    }

    val scheduleResult = mapSchedule(scheduleDays)
    if (scheduleResult is ScheduleMappingResult.Error) {
        return VenueCreateRequestBuildResult.Error(scheduleResult.message)
    }

    val servicesResult = mapServices(
        items = services,
        priceListMediaId = servicesPriceListMediaId,
        priceListUrl = servicesPriceListUrl
    )
    if (servicesResult is ServicesMappingResult.Error) {
        return VenueCreateRequestBuildResult.Error(servicesResult.message)
    }

    val latitude = locationData.latitude
    val longitude = locationData.longitude
    val location = VenueLocation(
        address = address,
        city = null,
        region = null,
        country = "Ukraine",
        coordinates = if (latitude != null && longitude != null) {
            GeoCoordinates(
                latitude = latitude,
                longitude = longitude
            )
        } else {
            null
        }
    )

    val gallery = galleryPhotos
        .filter { it.id.isUuidLike() }
        .map { photo ->
            VenueMediaRef(
                mediaId = photo.id,
                publicUrl = photo.imageUrl.ifBlank { null }
            )
        }

    return VenueCreateRequestBuildResult.Success(
        CreateVenueRequest(
            name = name,
            description = mainInfo.description.trim().ifEmpty { null },
            category = category,
            phone = phone,
            website = normalizeWebsite(mainInfo.website),
            email = ownerInfo.email.trim().ifEmpty { null },
            location = location,
            schedule = (scheduleResult as ScheduleMappingResult.Success).schedule,
            services = (servicesResult as ServicesMappingResult.Success).services,
            gallery = gallery,
            galleryMediaIds = emptyList(),
            submitForModeration = submitForModeration
        )
    )
}

internal fun VenueFormState.toUpdateVenueRequest(): VenueUpdateRequestBuildResult {
    return when (val createResult = toCreateVenueRequest()) {
        is VenueCreateRequestBuildResult.Error -> VenueUpdateRequestBuildResult.Error(createResult.message)
        is VenueCreateRequestBuildResult.Success -> {
            val request = createResult.request
            VenueUpdateRequestBuildResult.Success(
                UpdateVenueRequest(
                    name = request.name,
                    description = request.description,
                    category = request.category,
                    phone = request.phone,
                    website = request.website,
                    email = request.email,
                    location = request.location,
                    schedule = request.schedule,
                    services = request.services,
                    gallery = request.gallery,
                    galleryMediaIds = request.galleryMediaIds,
                    status = null
                )
            )
        }
    }
}

internal fun VenueFormState.toPatchVenueRequest(submitForModeration: Boolean): VenuePatchRequestBuildResult {
    return when (val updateResult = toUpdateVenueRequest()) {
        is VenueUpdateRequestBuildResult.Error -> VenuePatchRequestBuildResult.Error(updateResult.message)
        is VenueUpdateRequestBuildResult.Success -> {
            val request = updateResult.request
            VenuePatchRequestBuildResult.Success(
                PatchVenueRequest(
                    name = request.name,
                    description = request.description,
                    category = request.category,
                    phone = request.phone,
                    website = request.website,
                    email = request.email,
                    location = request.location,
                    schedule = request.schedule,
                    services = request.services,
                    gallery = request.gallery,
                    galleryMediaIds = request.galleryMediaIds,
                    submitForModeration = submitForModeration
                )
            )
        }
    }
}

internal fun VenueResponse.toVenueFormState(
    currentState: VenueFormState,
    mode: VenueFormMode,
    labels: VenueScheduleDayLabels
): VenueFormState {
    val mappedGallery = gallery.mapIndexed { index, media ->
        val url = media.publicUrl?.takeIf { it.isNotBlank() }.orEmpty()
        val id = media.mediaId?.takeIf { it.isNotBlank() }
            ?: media.publicUrl?.takeIf { it.isNotBlank() }
            ?: "gallery_$index"
        VenueGalleryPhoto(id = id, imageUrl = url)
    }

    val mappedServices = services.servicesList.mapIndexed { index, item ->
        VenueServiceItemData(
            id = "service_${index + 1}",
            name = item.name,
            price = item.price.toUiPrice(),
            currency = item.currency,
            description = item.description.orEmpty(),
            hasError = false
        )
    }

    val categoryOption = SelectOption(
        id = category.name,
        label = category.toFallbackLabel()
    )

    val coordinates = location.coordinates
    val scheduleByDay = schedule.workDays.associateBy { it.day }
    val orderedDays = listOf(
        Triple(DayOfWeek.MONDAY, "mon", labels.monday),
        Triple(DayOfWeek.TUESDAY, "tue", labels.tuesday),
        Triple(DayOfWeek.WEDNESDAY, "wed", labels.wednesday),
        Triple(DayOfWeek.THURSDAY, "thu", labels.thursday),
        Triple(DayOfWeek.FRIDAY, "fri", labels.friday),
        Triple(DayOfWeek.SATURDAY, "sat", labels.saturday),
        Triple(DayOfWeek.SUNDAY, "sun", labels.sunday)
    )

    val mappedSchedule = orderedDays.map { (dayOfWeek, id, label) ->
        val workDay = scheduleByDay[dayOfWeek]
        val isOpen = workDay?.isOpen == true
        WorkScheduleDay(
            id = id,
            label = label,
            isOpen = isOpen,
            openTime = if (isOpen) workDay.openTime?.toUiTime().orEmpty() else "",
            closeTime = if (isOpen) workDay.closeTime?.toUiTime().orEmpty() else ""
        )
    }

    val fileNameFromUrl = services.priceListUrl
        ?.substringAfterLast('/')
        ?.substringBefore('?')
        ?.takeIf { it.isNotBlank() }

    return currentState.copy(
        isInitialized = true,
        mode = mode,
        isLoading = false,
        isSubmitting = false,
        venueStatus = status,
        draftState = null,
        venueModeratorId = moderatorId,
        ownerId = ownerId,
        ownerReadOnly = true,
        mainInfo = VenueMainInfoData(
            name = name,
            category = categoryOption,
            phone = phone,
            website = website.orEmpty(),
            description = description.orEmpty()
        ),
        locationData = VenueLocationData(
            address = location.address,
            latitude = coordinates?.latitude,
            longitude = coordinates?.longitude,
            mapPreviewUrl = ""
        ),
        scheduleDays = mappedSchedule,
        galleryPhotos = mappedGallery,
        services = mappedServices,
        attachedServicesFileName = fileNameFromUrl,
        servicesPriceListMediaId = services.priceListMedia?.mediaId,
        servicesPriceListUrl = services.priceListUrl,
        nextServiceIndex = mappedServices.size + 1
    )
}

internal fun CreateVenueRequest.toVenueFormState(
    currentState: VenueFormState,
    mode: VenueFormMode,
    labels: VenueScheduleDayLabels
): VenueFormState {
    val resolvedGallery = if (gallery.isNotEmpty()) {
        gallery
    } else {
        galleryMediaIds.map { mediaId -> VenueMediaRef(mediaId = mediaId, publicUrl = null) }
    }
    val mappedGallery = resolvedGallery.mapIndexed { index, media ->
        val url = media.publicUrl?.takeIf { it.isNotBlank() }.orEmpty()
        val id = media.mediaId?.takeIf { it.isNotBlank() }
            ?: media.publicUrl?.takeIf { it.isNotBlank() }
            ?: "gallery_$index"
        VenueGalleryPhoto(id = id, imageUrl = url)
    }
    val mappedServices = services.servicesList.mapIndexed { index, item ->
        VenueServiceItemData(
            id = "service_${index + 1}",
            name = item.name,
            price = item.price.toUiPrice(),
            currency = item.currency,
            description = item.description.orEmpty(),
            hasError = false
        )
    }
    val categoryOption = SelectOption(
        id = category.name,
        label = category.toFallbackLabel()
    )
    val scheduleByDay = schedule.workDays.associateBy { it.day }
    val orderedDays = listOf(
        Triple(DayOfWeek.MONDAY, "mon", labels.monday),
        Triple(DayOfWeek.TUESDAY, "tue", labels.tuesday),
        Triple(DayOfWeek.WEDNESDAY, "wed", labels.wednesday),
        Triple(DayOfWeek.THURSDAY, "thu", labels.thursday),
        Triple(DayOfWeek.FRIDAY, "fri", labels.friday),
        Triple(DayOfWeek.SATURDAY, "sat", labels.saturday),
        Triple(DayOfWeek.SUNDAY, "sun", labels.sunday)
    )
    val mappedSchedule = orderedDays.map { (dayOfWeek, id, label) ->
        val workDay = scheduleByDay[dayOfWeek]
        val isOpen = workDay?.isOpen == true
        WorkScheduleDay(
            id = id,
            label = label,
            isOpen = isOpen,
            openTime = if (isOpen) workDay.openTime?.toUiTime().orEmpty() else "",
            closeTime = if (isOpen) workDay.closeTime?.toUiTime().orEmpty() else ""
        )
    }

    val coordinates = location.coordinates
    val fileNameFromUrl = services.priceListUrl
        ?.substringAfterLast('/')
        ?.substringBefore('?')
        ?.takeIf { it.isNotBlank() }

    return currentState.copy(
        isInitialized = true,
        mode = mode,
        isLoading = false,
        isSubmitting = false,
        venueStatus = null,
        draftState = null,
        venueModeratorId = null,
        ownerId = null,
        ownerReadOnly = true,
        ownerInfo = OwnerInfoData(email = email.orEmpty()),
        mainInfo = VenueMainInfoData(
            name = name,
            category = categoryOption,
            phone = phone,
            website = website.orEmpty(),
            description = description.orEmpty()
        ),
        locationData = VenueLocationData(
            address = location.address,
            latitude = coordinates?.latitude,
            longitude = coordinates?.longitude,
            mapPreviewUrl = ""
        ),
        scheduleDays = mappedSchedule,
        galleryPhotos = mappedGallery,
        services = mappedServices,
        attachedServicesFileName = fileNameFromUrl,
        servicesPriceListMediaId = services.priceListMedia?.mediaId,
        servicesPriceListUrl = services.priceListUrl,
        nextServiceIndex = mappedServices.size + 1
    )
}

internal fun VenueResponse.applyDraftPayload(payload: UpdateVenueRequest): VenueResponse {
    val resolvedGallery = if (payload.gallery.isNotEmpty()) {
        payload.gallery
    } else {
        payload.galleryMediaIds.map { mediaId ->
            VenueMediaRef(mediaId = mediaId, publicUrl = null)
        }
    }

    return this.copy(
        name = payload.name,
        description = payload.description,
        category = payload.category,
        phone = payload.phone,
        website = payload.website,
        email = payload.email,
        location = payload.location,
        schedule = payload.schedule,
        services = payload.services,
        gallery = resolvedGallery,
        galleryMediaIds = resolvedGallery.mapNotNull { it.mediaId }
    )
}

private sealed interface ScheduleMappingResult {
    data class Success(val schedule: VenueSchedule) : ScheduleMappingResult
    data class Error(val message: UiText) : ScheduleMappingResult
}

private fun mapSchedule(days: List<WorkScheduleDay>): ScheduleMappingResult {
    val workDays = mutableListOf<WorkDay>()

    days.forEach { day ->
        val mappedDay = day.id.toDomainDayOfWeek()
            ?: return ScheduleMappingResult.Error(
                UiText.StringResource(Res.string.venue_error_schedule_day_unknown, day.id)
            )

        if (day.isOpen) {
            val openRaw = day.openTime.trim()
            if (openRaw.isEmpty()) {
                return ScheduleMappingResult.Error(
                    UiText.StringResource(Res.string.venue_error_schedule_open_time_required, day.label)
                )
            }

            val closeRaw = day.closeTime.trim()
            if (closeRaw.isEmpty()) {
                return ScheduleMappingResult.Error(
                    UiText.StringResource(Res.string.venue_error_schedule_close_time_required, day.label)
                )
            }

            val openTime = parseLocalTime(openRaw)
                ?: return ScheduleMappingResult.Error(
                    UiText.StringResource(Res.string.venue_error_schedule_open_time_invalid, day.label)
                )
            val closeTime = parseLocalTime(closeRaw)
                ?: return ScheduleMappingResult.Error(
                    UiText.StringResource(Res.string.venue_error_schedule_close_time_invalid, day.label)
                )

            if (openTime.toMinutesOfDay() >= closeTime.toMinutesOfDay()) {
                return ScheduleMappingResult.Error(
                    UiText.StringResource(Res.string.venue_error_schedule_order_invalid, day.label)
                )
            }

            workDays += WorkDay(
                day = mappedDay,
                isOpen = true,
                openTime = openTime,
                closeTime = closeTime,
                breakStart = null,
                breakEnd = null
            )
        } else {
            workDays += WorkDay(
                day = mappedDay,
                isOpen = false,
                openTime = null,
                closeTime = null,
                breakStart = null,
                breakEnd = null
            )
        }
    }

    return ScheduleMappingResult.Success(VenueSchedule(workDays = workDays))
}

private sealed interface ServicesMappingResult {
    data class Success(val services: VenueServices) : ServicesMappingResult
    data class Error(val message: UiText) : ServicesMappingResult
}

private fun mapServices(
    items: List<VenueServiceItemData>,
    priceListMediaId: String?,
    priceListUrl: String?
): ServicesMappingResult {
    val mappedServices = mutableListOf<ServiceItem>()

    items.forEach { item ->
        val name = item.name.trim()
        val description = item.description.trim().ifEmpty { null }
        val priceText = item.price.trim()
        val currencyText = item.currency.trim().uppercase()

        val hasAnyValue = name.isNotEmpty() || description != null || priceText.isNotEmpty() || currencyText.isNotEmpty()
        if (!hasAnyValue) return@forEach

        if (name.isEmpty()) {
            return ServicesMappingResult.Error(UiText.StringResource(Res.string.venue_error_service_name_required))
        }

        val price = parsePrice(priceText)
            ?: return ServicesMappingResult.Error(
                UiText.StringResource(Res.string.venue_error_service_price_invalid, name)
            )

        val currency = currencyText.ifEmpty { "UAH" }
        if (!currency.matches(Regex("^[A-Z]{3}$"))) {
            return ServicesMappingResult.Error(
                UiText.StringResource(Res.string.venue_error_service_currency_invalid, name)
            )
        }

        mappedServices += ServiceItem(
            name = name,
            description = description,
            price = price,
            currency = currency
        )
    }

    return ServicesMappingResult.Success(
        VenueServices(
            servicesList = mappedServices,
            priceListMedia = priceListMediaId?.let { mediaId ->
                VenueMediaRef(
                    mediaId = mediaId,
                    publicUrl = priceListUrl
                )
            },
            priceListUrl = priceListUrl
        )
    )
}

private fun String.toDomainDayOfWeek(): DayOfWeek? {
    return when (lowercase()) {
        "mon" -> DayOfWeek.MONDAY
        "tue" -> DayOfWeek.TUESDAY
        "wed" -> DayOfWeek.WEDNESDAY
        "thu" -> DayOfWeek.THURSDAY
        "fri" -> DayOfWeek.FRIDAY
        "sat" -> DayOfWeek.SATURDAY
        "sun" -> DayOfWeek.SUNDAY
        else -> null
    }
}

private fun parseLocalTime(value: String): LocalTime? {
    val normalized = normalizeTime(value)
    return runCatching { LocalTime.parse(normalized) }.getOrNull()
}

private fun LocalTime.toMinutesOfDay(): Int {
    return (hour * 60) + minute
}

private fun LocalTime.toUiTime(): String {
    val hourPart = hour.toString().padStart(2, '0')
    val minutePart = minute.toString().padStart(2, '0')
    return "$hourPart:$minutePart"
}

private fun normalizeTime(value: String): String {
    val trimmed = value.trim()
    if (Regex("^\\d{1}:\\d{2}$").matches(trimmed)) {
        return "0$trimmed"
    }
    return trimmed
}

private fun parsePrice(value: String): Double? {
    if (value.isBlank()) return null
    val match = Regex("\\d+(?:[\\.,]\\d+)?").find(value) ?: return null
    val normalized = match.value.replace(',', '.')
    return normalized.toDoubleOrNull()
}

private fun normalizeWebsite(value: String): String? {
    val trimmed = value.trim()
    if (trimmed.isEmpty()) return null
    return if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        trimmed
    } else {
        "https://$trimmed"
    }
}

private fun String.isUuidLike(): Boolean {
    val uuidRegex = Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$")
    return uuidRegex.matches(this)
}

private fun VenueCategory.toFallbackLabel(): String {
    return name
        .lowercase()
        .split('_')
        .joinToString(" ") { part ->
            part.replaceFirstChar { ch -> ch.uppercase() }
        }
}

private fun Double.toUiPrice(): String {
    val normalized = toString()
    return if (normalized.endsWith(".0")) {
        normalized.removeSuffix(".0")
    } else {
        normalized
    }
}
